/**
 * LegacySubscriptionCard - Display for pre-migration subscriptions without bundle metadata
 */
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import InventoryIcon from "@mui/icons-material/Inventory";
import CancelIcon from "@mui/icons-material/Cancel";
import type {
  VariantIndexItem,
  LegacySubscription,
  RecipeGridItem,
} from "../utils/recharge";
import { QuizStyledCard } from "./shared/QuizStyledCard";
import { QUIZ } from "@/theme/quizTokens";

function statusChip(status?: string) {
  const s = String(status || "unknown").toLowerCase();
  const label =
    s === "active"
      ? "Active"
      : s === "cancelled"
      ? "Cancelled"
      : s === "paused"
      ? "Paused"
      : s;

  const bgColor =
    s === "active"
      ? "#E5FFEA"
      : s === "paused"
      ? "#FFF0D6"
      : s === "cancelled"
      ? "#FFE5E2"
      : "#E6F0FF";

  const textColor =
    s === "active"
      ? "#2E7D32"
      : s === "paused"
      ? "#CE7A00"
      : s === "cancelled"
      ? "#D14C3F"
      : "#1A57D3";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bgColor,
        color: textColor,
        fontWeight: 700,
        textTransform: "capitalize",
        height: 28,
        "& .MuiChip-label": { px: 1.5, fontSize: 12 },
      }}
    />
  );
}

export function LegacySubscriptionCard({
  sub,
  legacy,
  productInfo,
  meals,
  onEditMeals,
  onChangeQuantity,
  onStopPlan,
}: {
  sub: any;
  legacy: LegacySubscription;
  productInfo: VariantIndexItem | null;
  meals: RecipeGridItem[];
  onEditMeals: () => void;
  onChangeQuantity?: () => void;
  onStopPlan?: () => void;
}) {
  const isActive = String(sub?.status || "").toLowerCase() === "active";

  return (
    <QuizStyledCard variant="white">
      {/* Top row with product image and details */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "flex-start",
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box sx={{ display: "flex", gap: 2, minWidth: 0, flex: 1 }}>
          {/* Product Image */}
          <Box
            sx={{
              width: { xs: 64, md: 80 },
              height: { xs: 64, md: 80 },
              borderRadius: { xs: "12px", md: "16px" },
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,.10)",
              background: QUIZ.color.cream,
              flexShrink: 0,
            }}
          >
            {productInfo?.imageUrl ? (
              <img
                src={productInfo.imageUrl}
                alt={legacy.productTitle}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RestaurantIcon sx={{ fontSize: 32, color: QUIZ.color.brown, opacity: 0.3 }} />
              </Box>
            )}
          </Box>

          {/* Product Details */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5, flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontSize: { xs: 18, md: 20 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                {legacy.productTitle}
              </Typography>
              {statusChip(sub?.status)}
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.65,
                mb: 0.5,
              }}
            >
              {legacy.variantTitle} • Qty: {legacy.quantity} • Every {(() => {
                const freq = Number(sub?.order_interval_frequency || 1);
                const unit = String(sub?.order_interval_unit || "day").toLowerCase();
                if (unit === "week" || unit === "weeks") {
                  const days = freq * 7;
                  return `${days} day${days > 1 ? "s" : ""}`;
                }
                return `${freq} ${unit}${freq > 1 ? "s" : ""}`;
              })()}
            </Typography>
          </Box>
        </Box>

        {/* Price - Right Side, Bold */}
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 28 },
              fontWeight: 900,
              color: QUIZ.color.brown,
              lineHeight: 1,
            }}
          >
            £{(Number(legacy.price) * Number(legacy.quantity || 1)).toFixed(2)}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 11, md: 12 },
              color: QUIZ.color.brown,
              opacity: 0.5,
              mt: 0.5,
            }}
          >
            per delivery
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ my: { xs: 2, md: 3 }, opacity: 0.3 }} />

      {/* Management Actions - Icon Button Grid */}
      {isActive && (() => {
        // Delivery actions are now global (handled in DeliveryScheduleCard)
        const managementActions = [
          { label: "Change quantity", icon: InventoryIcon, onClick: onChangeQuantity, color: QUIZ.color.brown },
          { label: "Stop Plan", icon: CancelIcon, onClick: onStopPlan, color: "#D14C3F" },
        ].filter(a => a.onClick);

        return managementActions.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {managementActions.map((action) => {
              const IconComponent = action.icon;
              return (
                <Box
                  key={action.label}
                  onClick={action.onClick}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    p: { xs: 1.5, md: 2 },
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: QUIZ.color.cream,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: QUIZ.color.cream2,
                      borderColor: action.color,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: `${action.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComponent sx={{ fontSize: 18, color: action.color }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12 },
                      fontWeight: 600,
                      color: QUIZ.color.brown,
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {action.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null;
      })()}

      {/* Meal Details */}
      {meals.length > 0 && (
        <>
          <Divider sx={{ my: { xs: 2, md: 3 }, opacity: 0.3 }} />
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 15 },
                fontWeight: 700,
                color: QUIZ.color.brown,
                mb: 1.5,
              }}
            >
              Current Items
            </Typography>
            <Stack spacing={1}>
              {meals.map((meal) => (
                <Box
                  key={meal.variantId}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  {/* Meal Image */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,.08)",
                      flexShrink: 0,
                    }}
                  >
                    {meal.imageUrl ? (
                      <img
                        src={meal.imageUrl}
                        alt={meal.productTitle}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          background: QUIZ.color.cream,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <RestaurantIcon sx={{ fontSize: 16, color: QUIZ.color.brown, opacity: 0.3 }} />
                      </Box>
                    )}
                  </Box>

                  {/* Meal Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: QUIZ.color.brown,
                        lineHeight: 1.3,
                      }}
                    >
                      {meal.productTitle}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: QUIZ.color.brown,
                        opacity: 0.65,
                      }}
                    >
                      {meal.variantTitle}
                    </Typography>
                  </Box>

                  {/* Quantity Badge */}
                  <Chip
                    label={`× ${meal.count}`}
                    size="small"
                    sx={{
                      background: QUIZ.color.blue,
                      color: "#1A57D3",
                      fontWeight: 700,
                      height: 24,
                      fontSize: 12,
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </QuizStyledCard>
  );
}
